import { Button } from '@solinkcorp/solink-ui/components/Button';
import {
  Combobox,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@solinkcorp/solink-ui/components/Combobox';
import { DialogComboboxContent } from '@solinkcorp/solink-ui/components/DialogCombobox';
import { NumberInput } from '@solinkcorp/solink-ui/components/NumberInput';
import { StableHeightSuspense } from '@solinkcorp/solink-ui/components/StableHeightSuspense';
import { Textarea } from '@solinkcorp/solink-ui/components/Textarea';
import { Field, FieldLabel } from '@solinkcorp/solink-ui/components/ui/field';
import { ToggleGroup, ToggleGroupItem } from '@solinkcorp/solink-ui/components/ui/toggle-group';
import { Plus } from 'lucide-react';
import { useState, type ChangeEvent, type FC } from 'react';
import { type Classification, type DatasetExpectedOutput } from 'vd-server/db/aiDatasetSchema';
import { useDatasets } from '../../DatasetsPage/hooks/useDatasets';
import { useClientVD } from '../../hooks/useClientVD';
import { honoFetcher } from '../../lib/honoFetcher';
import { pollForJob } from '../../lib/pollForJob';
import { DatasetExpectedOutputs } from './DatasetExpectedOutputs';

const classificationValues = ['present', 'absent'];
const analysisModeValues = ['image', 'video'] as const;
type DatasetAnalysisMode = (typeof analysisModeValues)[number];

interface EventIdItemPost {
  type: 'eventid';
  eventId: string;
}

interface EventItemPost {
  type: 'event';
  event: {
    startTime: string;
    endTime: string;
    cameras: Record<string, unknown>[];
    siteId: string;
  };
}

export type ItemPost = EventIdItemPost | EventItemPost;

export interface AddToDatasetProps {
  runId?: string;
  item: ItemPost;
  onSuccess?: () => void;
}

export const AddToDataset: FC<AddToDatasetProps> = ({ item, runId, onSuccess }) => {
  const { datasets } = useDatasets();
  const [selectedDataset, setSelectedDataset] = useState<string>();
  const [classification, setClassification] = useState<Classification>();
  const [analysisMode, setAnalysisMode] = useState<DatasetAnalysisMode>('video');
  const [description, setDescription] = useState('');
  const [startOffset, setStartOffset] = useState(10);
  const [endOffset, setEndOffset] = useState(10);
  const client = useClientVD();
  const [expectedOutputs, setExpectedOutputs] = useState<Record<string, DatasetExpectedOutput>>({});

  const handleAddToDataset = async () => {
    if (!selectedDataset || !classification) return;
    const { jobId, queueName } = await honoFetcher(
      client.datasets[':datasetId'].items.$post({
        param: { datasetId: selectedDataset },
        json: {
          classification,
          description,
          expectedOutputs: Object.values(expectedOutputs),
          runId,
          startOffset: analysisMode === 'video' ? startOffset : 0,
          endOffset: analysisMode === 'video' ? endOffset : 0,
          analysisMode,
          ...item,
        },
      }),
    );

    await pollForJob(client, queueName, [jobId]);
    setSelectedDataset(undefined);
    setClassification(undefined);
    setAnalysisMode('video');
    setDescription('');
    setExpectedOutputs({});
    setStartOffset(10);
    setEndOffset(10);
    onSuccess?.();
  };

  return (
    <>
      <Field>
        <FieldLabel>Target Dataset</FieldLabel>
        <Combobox
          items={datasets}
          value={datasets.find((ds) => ds.datasetId === selectedDataset) ?? null}
          onValueChange={(dataset) => setSelectedDataset(dataset?.datasetId)}
          itemToStringLabel={(dataset) => dataset.name}
          data-testid="agent-overview-dataset-select"
        >
          <ComboboxInput placeholder="Select dataset..." className="w-full" />
          <DialogComboboxContent>
            <ComboboxEmpty>No datasets found.</ComboboxEmpty>
            <ComboboxList<(typeof datasets)[number]>>
              {(dataset) => (
                <ComboboxItem key={dataset.datasetId} value={dataset}>
                  {dataset.name}
                </ComboboxItem>
              )}
            </ComboboxList>
          </DialogComboboxContent>
        </Combobox>
      </Field>

      <Field>
        <FieldLabel>Classification</FieldLabel>
        <ToggleGroup
          type="single"
          value={classification}
          onValueChange={(val: Classification) => setClassification(val)}
          data-testid="agent-overview-classification-input"
        >
          {classificationValues.map((type) => (
            <ToggleGroupItem
              key={type}
              value={type}
              variant="outline"
              className="text-xs font-medium capitalize"
            >
              {type}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </Field>

      <Field>
        <FieldLabel>Media type</FieldLabel>
        <ToggleGroup
          type="single"
          value={analysisMode}
          onValueChange={(val: DatasetAnalysisMode) => setAnalysisMode(val)}
          data-testid="agent-overview-analysis-mode-input"
        >
          {analysisModeValues.map((mode) => (
            <ToggleGroupItem
              key={mode}
              value={mode}
              variant="outline"
              className="text-xs font-medium capitalize"
            >
              {mode}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </Field>

      {analysisMode === 'video' && (
        <Field>
          <FieldLabel>Clip buffer (seconds)</FieldLabel>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <span className="text-xs text-muted-foreground">
                Seconds included before the event start
              </span>
              <NumberInput
                min={0}
                max={300}
                value={startOffset}
                onChange={setStartOffset}
                data-testid="agent-overview-start-offset-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-xs text-muted-foreground">
                Seconds included after the event end
              </span>
              <NumberInput
                min={0}
                max={300}
                value={endOffset}
                onChange={setEndOffset}
                data-testid="agent-overview-end-offset-input"
              />
            </div>
          </div>
        </Field>
      )}

      <Field>
        <FieldLabel>Description</FieldLabel>
        <Textarea
          value={description}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
          placeholder="Enter details about this event..."
          data-testid="agent-overview-description-input"
        />
      </Field>

      <StableHeightSuspense>
        <DatasetExpectedOutputs
          datasetId={selectedDataset}
          expectedOutputs={expectedOutputs}
          onChange={setExpectedOutputs}
        />
      </StableHeightSuspense>

      <Button
        className="w-full"
        onClickAsync={handleAddToDataset}
        disabled={!selectedDataset || !classification}
        data-testid="agent-overview-add-to-dataset-button"
        loadingText="Adding"
      >
        <Plus size={16} /> Add to Dataset
      </Button>
    </>
  );
};
